import { Controller } from "@hotwired/stimulus"

// Maintains chronological order of chat messages when they arrive out of order
// via ActionCable broadcasts, and auto-scrolls to bottom on new content
// eslint-disable-next-line import/no-default-export
export default class extends Controller {
  static targets = ["message"]

  connect() {
    this.reorderMessages()
    this.observeNewMessages()
    this.observeContentChanges()
    this.setupTurboStreamListener()
    this.scrollToBottom()
  }

  observeNewMessages() {
    const observer = new MutationObserver((mutations) => {
      let shouldReorder = false
      let newThinkingMessage = null

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeType === 1 &&
            node.matches('[data-message-ordering-target="message"]')
          ) {
            // Check for duplicate message IDs and remove the duplicate
            if (this.removeDuplicateMessage(node)) {
              return // Skip further processing for removed duplicate
            }

            shouldReorder = true
            // Check if this new message has a thinking indicator
            if (node.querySelector(".thinking")) {
              newThinkingMessage = node
            }
          }
        })
      })

      // If a new thinking message was added, remove any previous empty bubbles
      if (newThinkingMessage) {
        this.removeOrphanedEmptyBubbles(newThinkingMessage)
      }

      if (shouldReorder) {
        // Avoid feedback loop from reordering itself triggering new mutations
        observer.disconnect()
        setTimeout(() => {
          this.reorderMessages()
          observer.observe(this.element, { childList: true, subtree: true })
        }, 10)
      }
    })

    observer.observe(this.element, { childList: true, subtree: true })
    this.messageObserver = observer
  }

  removeDuplicateMessage(newNode) {
    // Get the turbo frame ID (e.g., "message_123")
    const newId = newNode.id
    if (!newId) return false

    // Find any existing message with the same ID
    const existingMessages = this.element.querySelectorAll(
      `[data-message-ordering-target="message"]#${CSS.escape(newId)}`
    )

    // If there's more than one element with this ID (the new one + existing)
    // we need to deduplicate
    if (existingMessages.length > 1) {
      // Keep the one with more content (or the newer one if both empty)
      const [first, second] = existingMessages
      const firstHasContent = !this.isMessageEmpty(first)
      const secondHasContent = !this.isMessageEmpty(second)

      if (firstHasContent && !secondHasContent) {
        second.remove()
        return newNode === second
      } else if (!firstHasContent && secondHasContent) {
        first.remove()
        return newNode === first
      } else {
        // Both have same content state, remove the duplicate (newer one)
        newNode.remove()
        return true
      }
    }

    return false
  }

  removeOrphanedEmptyBubbles(excludeElement) {
    // Find all message frames with thinking indicators (empty messages)
    const emptyMessages = this.element.querySelectorAll(
      '[data-message-ordering-target="message"]'
    )

    emptyMessages.forEach((message) => {
      // Skip the newly added message
      if (message === excludeElement) return

      // If this message has a thinking indicator, remove it from the DOM
      if (message.querySelector(".thinking")) {
        message.remove()
      }
    })
  }

  observeContentChanges() {
    // Observe for any content changes (handles streaming content updates)
    const observer = new MutationObserver(() => {
      this.scrollToBottom()
    })

    observer.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    this.contentObserver = observer
  }

  setupTurboStreamListener() {
    // Listen for Turbo Stream events (handles broadcast_replace_to)
    this.boundTurboHandler = () => {
      requestAnimationFrame(() => this.scrollToBottom())
    }
    document.addEventListener(
      "turbo:before-stream-render",
      this.boundTurboHandler
    )
  }

  disconnect() {
    if (this.messageObserver) {
      this.messageObserver.disconnect()
    }
    if (this.contentObserver) {
      this.contentObserver.disconnect()
    }
    if (this.boundTurboHandler) {
      document.removeEventListener(
        "turbo:before-stream-render",
        this.boundTurboHandler
      )
    }
  }

  reorderMessages() {
    const messages = Array.from(this.messageTargets)

    // Hide empty assistant messages BEFORE reordering to prevent flash
    this.hideEmptyAssistantMessages(messages)

    messages.sort((a, b) => {
      const timeA = new Date(a.dataset.createdAt).getTime()
      const timeB = new Date(b.dataset.createdAt).getTime()
      // Handle invalid dates
      if (isNaN(timeA) && isNaN(timeB)) return 0
      if (isNaN(timeA)) return 1  // Move invalid to end
      if (isNaN(timeB)) return -1
      return timeA - timeB
    })

    messages.forEach((message) => {
      this.element.appendChild(message)
    })

    this.scrollToBottom()
  }

  isMessageEmpty(message) {
    // Check the data attribute first (set at render time)
    if (message.dataset.contentEmpty === "false") {
      return false
    }
    // Also check if content has actually been streamed in
    // (the data attribute isn't updated when content streams in)
    const contentDiv = message.querySelector(".message-content")
    if (contentDiv) {
      // If it has a thinking indicator, it's still empty
      if (contentDiv.querySelector(".thinking")) {
        return true
      }
      // If it has actual text content, it's not empty
      const textContent = contentDiv.textContent?.trim()
      if (textContent && textContent.length > 0) {
        return false
      }
    }
    return message.dataset.contentEmpty === "true"
  }

  hideEmptyAssistantMessages(messages) {
    // Find all assistant messages that are actually empty
    const emptyAssistantMessages = messages.filter(
      (m) => m.dataset.role === "assistant" && this.isMessageEmpty(m)
    )

    // Find the last assistant message overall (may or may not be empty)
    const lastAssistantMessage = [...messages]
      .reverse()
      .find((m) => m.dataset.role === "assistant")

    // Hide empty assistant messages, except the last assistant message
    emptyAssistantMessages.forEach((message) => {
      if (message === lastAssistantMessage) {
        message.style.display = ""
      } else {
        message.style.display = "none"
      }
    })
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight
  }
}
